<template>
  <!-- Dev mode: no captcha needed -->
  <template v-if="provider === 'none'" />
  <GeetestCheckbox
    v-else-if="provider === 'geetest'"
    ref="widgetRef"
    :container-id="containerId"
    :auto-show="autoShow"
    @verified="onVerified"
    @expired="onExpired"
    @error="onError"
  />
  <TurnstileCheckbox
    v-else-if="provider === 'turnstile'"
    ref="widgetRef"
    :container-id="containerId"
    @verified="onVerified"
    @expired="onExpired"
    @error="onError"
  />
  <RecaptchaCheckbox
    v-else
    ref="widgetRef"
    :container-id="containerId"
    @verified="onVerified"
    @expired="onExpired"
    @error="onError"
  />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { getCaptchaProvider, type CaptchaProvider } from '@/shared/utils/captchaConfig'
import { getInvisibleTurnstileToken } from '@/shared/utils/turnstile'
import RecaptchaCheckbox from './RecaptchaCheckbox.vue'
import TurnstileCheckbox from './TurnstileCheckbox.vue'
import GeetestCheckbox from './GeetestCheckbox.vue'

const props = withDefaults(defineProps<{
  containerId?: string
  autoShow?: boolean
  /**
   * HK-IGRA.940: сначала невидимая Turnstile, видимый провайдер — только если
   * её не хватило. Выключается там, где виджета Turnstile для домена нет.
   */
  invisibleFirst?: boolean
}>(), {
  invisibleFirst: true,
})

const emit = defineEmits<{
  (e: 'verified', token: string): void
  (e: 'expired'): void
  (e: 'error'): void
}>()

const provider = computed<CaptchaProvider>(() => getCaptchaProvider())

const widgetRef = ref<InstanceType<typeof RecaptchaCheckbox> | InstanceType<typeof TurnstileCheckbox> | InstanceType<typeof GeetestCheckbox> | null>(null)

const onVerified = (token: string) => {
  emit('verified', token)
}

const onExpired = () => {
  emit('expired')
}

const onError = () => {
  emit('error')
}

/**
 * Reset the widget for re-verification
 */
const reset = () => {
  widgetRef.value?.reset()
}

/**
 * Get current token
 */
const getToken = (): string => {
  return widgetRef.value?.getToken() ?? ''
}

/**
 * Trigger captcha popup (for Geetest bind mode).
 * In dev mode (provider=none), immediately emit verified with a bypass token.
 *
 * HK-IGRA.940: перед видимым окном — невидимая Turnstile. Прошла — токен
 * уходит сразу, человек ничего не видит. Нет — показываем видимого провайдера.
 * Если потом отказал бэкенд, вызывающий зовёт requireVisibleCaptcha() и
 * showCaptcha() ещё раз — второй раз невидимая уже не пробуется.
 */
const showCaptcha = async () => {
  if (provider.value === 'none') {
    emit('verified', 'dev-bypass')
    return
  }
  if (props.invisibleFirst) {
    const token = await getInvisibleTurnstileToken()
    if (token) {
      emit('verified', token)
      return
    }
  }
  widgetRef.value?.showCaptcha?.()
}

// Expose methods for parent components
defineExpose({
  reset,
  getToken,
  showCaptcha,
  provider,
})
</script>

